import { PublicClientApplication, Configuration, LogLevel } from '@azure/msal-browser';

/**
 * MSAL Configuration for Azure AD / Entra ID authentication
 *
 * SECURITY NOTE: Client ID and Tenant ID are not secrets - they're safe in frontend code.
 * Actual authentication happens via Azure AD's secure OAuth flow.
 */
const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID || 'common'}`,
    redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // sessionStorage is more secure than localStorage
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          case LogLevel.Info:
            // Only log info in development
            if (import.meta.env.DEV) {
              console.info(message);
            }
            break;
          case LogLevel.Verbose:
            if (import.meta.env.DEV) {
              console.debug(message);
            }
            break;
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Verbose : LogLevel.Warning,
      piiLoggingEnabled: false,
    },
    allowNativeBroker: false,
    tokenRenewalOffsetSeconds: 300, // Renew tokens 5 mins before expiry
  },
};

// Scopes for API access
export const loginRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email'],
};

// API scopes for backend calls
export const apiScopes = {
  scopes: [`api://${import.meta.env.VITE_AZURE_CLIENT_ID}/access_as_user`],
};

// Microsoft Graph scopes for sending emails
export const graphMailScopes = {
  scopes: ['Mail.Send'],
};

// Create MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Helper to get access token for API calls
export async function getAccessToken(): Promise<string | null> {
  const accounts = msalInstance.getAllAccounts();

  if (accounts.length === 0) {
    return null;
  }

  try {
    // Try to get token with API scope first
    const response = await msalInstance.acquireTokenSilent({
      ...apiScopes,
      account: accounts[0],
    });
    return response.accessToken;
  } catch (error) {
    console.warn('Failed to acquire API token, trying basic scopes:', error);

    // Fall back to basic login scopes (ID token)
    try {
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return response.idToken || response.accessToken;
    } catch (fallbackError) {
      console.error('Failed to acquire any token:', fallbackError);
      return null;
    }
  }
}

// Helper to get token for Microsoft Graph API (for sending emails)
export async function getGraphToken(): Promise<string | null> {
  const accounts = msalInstance.getAllAccounts();

  if (accounts.length === 0) {
    return null;
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...graphMailScopes,
      account: accounts[0],
    });
    return response.accessToken;
  } catch (error) {
    // If silent fails, try interactive
    try {
      const response = await msalInstance.acquireTokenPopup({
        ...graphMailScopes,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (popupError) {
      console.error('Failed to acquire Graph token:', popupError);
      return null;
    }
  }
}

// Send an email via Microsoft Graph
export async function sendEmail(
  to: string,
  subject: string,
  htmlBody: string
): Promise<boolean> {
  const token = await getGraphToken();
  if (!token) {
    console.error('No Graph token available for sending email');
    return false;
  }

  try {
    const response = await fetch('https://graph.microsoft.com/v1.0/me/sendMail', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: {
          subject,
          body: {
            contentType: 'HTML',
            content: htmlBody,
          },
          toRecipients: [
            {
              emailAddress: {
                address: to,
              },
            },
          ],
        },
        saveToSentItems: true,
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      console.error('Failed to send email:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error sending email:', error);
    return false;
  }
}
